import { instance } from "api/apiBase";
import {
  GetTasksParams,
  Task,
  ActivityLogsPayload,
  SortKey,
  TaskSubject,
  CreateTaskPayload,
  GetCountsBySubjectType,
  SubjectTypeCount,
  ActivityNote,
  ActivityLog
} from "shared/app/TaskTableView/types";
import { PaginatedResponse } from "shared/types";
import { Property } from "types/landlord/address";
import { User } from "views/settings-users/types";

export type RelatedObject = {
  id: number;
  type: TaskSubject;
  name: string;
  property: Property | null;
};

type RelatedObjectsPayload = PaginatedResponse<RelatedObject>;

const appendCommonFilterParams = (
  sp: URLSearchParams,
  params: GetCountsBySubjectType
) => {
  if (params.created_at?.from)
    sp.append("created_at[from]", params.created_at.from);
  if (params.created_at?.to) sp.append("created_at[to]", params.created_at.to);

  if (params.due_date?.from) sp.append("due_date[from]", params.due_date.from);
  if (params.due_date?.to) sp.append("due_date[to]", params.due_date.to);

  params.statuses.forEach((s) => sp.append("statuses[]", s));

  params.assignees?.forEach((a) => {
    sp.append("assignees[][subject_type]", String(a.subject_type));
    if (a.subject_type === "partner_user")
      sp.append("assignees[][subject_id]", String(a.subject_id));
  });

  params.creators?.forEach((c) => {
    sp.append("creators[][subject_type]", String(c.subject_type));
    if (c.subject_type === "partner_user")
      sp.append("creators[][subject_id]", String(c.subject_id));
  });
};

const toQueryString = (sp: URLSearchParams) => {
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

const buildTasksQuery = (params: GetTasksParams) => {
  const sp = new URLSearchParams();

  if (params.search) sp.append("search", params.search);

  params.subjects?.forEach((s) => {
    sp.append("subjects[][subject_type]", s.subject_type);
    if (s.subject_id) sp.append("subjects[][subject_id]", String(s.subject_id));
  });

  appendCommonFilterParams(sp, params);

  params.sorts?.forEach((s) => {
    (Object.keys(s) as SortKey[]).forEach((k) => {
      const value = s[k];
      if (value) sp.append(`sorts[][${k}]`, value);
    });
  });

  sp.append("page", String(params.page));
  sp.append("per_page", String(params.per_page));

  return toQueryString(sp);
};

const buildCountsQuery = (params: GetCountsBySubjectType) => {
  const sp = new URLSearchParams();
  appendCommonFilterParams(sp, params);
  return toQueryString(sp);
};

export const api = {
  getTasks: async (
    params: GetTasksParams
  ): Promise<PaginatedResponse<Task>> => {
    const qs = buildTasksQuery(params);
    const { data } = await instance.get<PaginatedResponse<Task>>(
      `/portal/v1/tasks${qs}`
    );
    return data;
  },

  createTask: async (payload: CreateTaskPayload): Promise<Task> => {
    const { data } = await instance.post<Task>("/portal/v1/tasks", payload);
    return data;
  },
  getCountBySubjectType: async (
    payload?: Partial<GetCountsBySubjectType>
  ): Promise<SubjectTypeCount> => {
    const qs = payload
      ? buildCountsQuery(payload as GetCountsBySubjectType)
      : "";
    const { data } = await instance.get<SubjectTypeCount>(
      `/portal/v1/tasks/counts_by_subject_type${qs}`
    );
    return data;
  },
  getRelatedObjects: async ({
    search = ""
  }: {
    search?: string;
  } = {}): Promise<RelatedObjectsPayload> => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    const { data } = await instance.get<RelatedObjectsPayload>(
      `/portal/v1/tasks/related_objects${qs}`
    );
    return data;
  },

  getTask: async (id: number): Promise<Task> => {
    const { data } = await instance.get<Task>(`/portal/v1/tasks/${id}`);
    return data;
  },

  updateTask: async (
    id: number,
    payload: Partial<CreateTaskPayload>
  ): Promise<Task> => {
    const { data } = await instance.patch<Task>(
      `/portal/v1/tasks/${id}`,
      payload
    );
    return data;
  },

  completeTask: async (id: number): Promise<Task> => {
    const { data } = await instance.post<Task>(
      `/portal/v1/tasks/${id}/complete`
    );
    return data;
  },

  reopenTask: async (id: number): Promise<Task> => {
    const { data } = await instance.post<Task>(`/portal/v1/tasks/${id}/reopen`);
    return data;
  },

  deleteTask: async (id: number): Promise<void> => {
    await instance.delete(`/portal/v1/tasks/${id}`);
  },

  undoDeleteTask: async (id: number): Promise<unknown> => {
    const { data } = await instance.post<unknown>(
      `/portal/v1/tasks/${id}/undo_delete`
    );
    return data;
  },
  getTaskActivityLogs: async (id: number): Promise<ActivityLogsPayload> => {
    const { data } = await instance.get<ActivityLogsPayload>(
      `/portal/v1/tasks/${id}/activity_log`
    );
    return data;
  },
  getMentionNotes: async (id: number): Promise<ActivityLogsPayload> => {
    const { data } = await instance.get<ActivityLogsPayload>(
      `/portal/v1/tasks/${id}/activity_log/mentions`
    );
    return data;
  },

  getAssignees: async (search?: string): Promise<User[]> => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    const { data } = await instance.get<User[]>(
      `/portal/v1/tasks/assignees${qs}`
    );
    return data;
  },

  getCreators: async (search?: string): Promise<User[]> => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    const { data } = await instance.get<User[]>(
      `/portal/v1/tasks/creators${qs}`
    );
    return data;
  },
  createNote: async (
    id: number,
    payload: ActivityNote
  ): Promise<ActivityLog> => {
    const { data } = await instance.post<ActivityLog>(
      `/portal/v1/tasks/${id}/activity_log`,
      payload
    );
    return data;
  }
};
